import React, { useEffect, useState } from 'react'
import { Button, Flex, Input, Tag, Typography } from 'antd'
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import { ActiveFilterTag } from './types'

const { Text } = Typography

const FilterDivider: React.FC = () => (
  <div
    style={{
      height: 1,
      background: 'var(--crm-border, #e2e5ec)',
      margin: '10px 0',
    }}
  />
)

type FilterSearchInputProps = {
  placeholder: string
  value: string
  onApply: (value: string) => void
  width?: number
  numericOnly?: boolean
}

// Applied explicitly (Enter, blur, clear) — applying on every keystroke would
// fire a request per character.
export const FilterSearchInput: React.FC<FilterSearchInputProps> = ({
  placeholder,
  value,
  onApply,
  width = 200,
  numericOnly = false,
}) => {
  const [draft, setDraft] = useState(value)

  // The URL is the source of truth: "Reset all" and browser navigation change
  // it from the outside and the input has to follow.
  useEffect(() => {
    setDraft(value)
  }, [value])

  return (
    <Input
      prefix={
        <SearchOutlined
          style={{ color: 'var(--color-gray-60)', fontSize: 12 }}
        />
      }
      placeholder={placeholder}
      inputMode={numericOnly ? 'numeric' : undefined}
      value={draft}
      onChange={(event) =>
        setDraft(
          numericOnly
            ? event.target.value.replace(/\D/g, '')
            : event.target.value,
        )
      }
      onPressEnter={() => onApply(draft.trim())}
      onBlur={() => onApply(draft.trim())}
      allowClear
      onClear={() => {
        setDraft('')
        onApply('')
      }}
      style={{ width }}
    />
  )
}

type ActiveFilterTagsProps = {
  tags: ActiveFilterTag[]
  onReset?: () => void
}

export const ActiveFilterTags: React.FC<ActiveFilterTagsProps> = ({
  tags,
  onReset,
}) => (
  <Flex gap={6} wrap="wrap" align="center">
    <Text
      type="secondary"
      style={{
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        whiteSpace: 'nowrap',
      }}
    >
      Active:
    </Text>
    {tags.map((tag) => (
      <Tag
        key={tag.key}
        closable
        onClose={tag.clear}
        style={{ fontSize: 11, margin: 0 }}
        color="blue"
      >
        {tag.label}
      </Tag>
    ))}
    {onReset && (
      <Button
        size="small"
        type="text"
        icon={<ReloadOutlined />}
        onClick={onReset}
        style={{ color: '#ef4444', fontSize: 11 }}
      >
        Reset all
      </Button>
    )}
  </Flex>
)

type FiltersPanelProps = {
  rows: React.ReactNode[]
  tags?: ActiveFilterTag[]
  onReset?: () => void
}

export const FiltersPanel: React.FC<FiltersPanelProps> = ({
  rows,
  tags = [],
  onReset,
}) => (
  <div
    style={{
      borderRadius: 8,
      padding: '12px 14px',
      marginBottom: 12,
      border: '1px solid var(--crm-border, #d9d9d9)',
      background: 'var(--crm-bg, #fff)',
    }}
  >
    {rows.map((row, index) => (
      // The rows are fixed positions in a static layout, never reordered.
      // eslint-disable-next-line react/no-array-index-key
      <React.Fragment key={index}>
        {index > 0 && <FilterDivider />}
        {row}
      </React.Fragment>
    ))}

    {tags.length > 0 && (
      <>
        <FilterDivider />
        <ActiveFilterTags tags={tags} onReset={onReset} />
      </>
    )}
  </div>
)
